import React, { useRef, useState, useEffect, createContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import TopBar from './components/TopBar';
import SideNav from './components/SideNav';
import MainContainer from './components/MainContainer';
import SettingsDrawer from './components/SettingsDrawer';
import Dock from './components/Dock';
import StartPagePrompt from './components/popup/StartPagePrompt';
import LoginModal from './components/popup/LoginModal';
import TopRightActions from './components/top-right-actions/TopRightActions';
import { appConfig } from './config/defaultData';
import { loadNavItemsOptimized } from './redux/slices/navSlice';
import { fetchStartPagePromptStatus, setStartPagePromptStatus } from './redux/slices/settingsSlice';
import { setLastSelectedNav } from './redux/slices/websiteSlice';
import './App.css';
import logger from './utils/logger';
import storage from './utils/LocalStorageManager';
import { message } from 'antd';
import trackingManager from './services/trackingManager';
import { logoutUser } from './redux/slices/authSlice';

export const GlobalMessageApiContext = createContext(null);

function App() {
  // 直接从 Redux 获取认证状态
  const { isLoggedIn, user } = useSelector(state => state.auth);
  // Redux
  const dispatch = useDispatch();
  const { lastSelectedNav } = useSelector(state => state.website);
  const { navItems } = useSelector(state => state.nav);
  const mainContainerRef = useRef(null);
  const dockRef = useRef(null);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [showStartPagePrompt, setShowStartPagePrompt] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(() => {
    // 从配置确定是否需要记住上次选中的菜单
    if (appConfig.rememberLastSelectedMenu) {
      // 使用 Redux 获取上次选中的菜单项，默认为'home'
      return lastSelectedNav || 'home';
    }
    return 'home';
  });
  const loadingRef = useRef(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [showLoginModal, setShowLoginModal] = useState(false);

  // 初始化加载
  useEffect(() => {
    if (loadingRef.current) {
      return;
    }
    
    loadingRef.current = true;
    
    // 记录页面加载开始时间
    const loadStartTime = performance.now();
    
    // 加载导航菜单项
    dispatch(loadNavItemsOptimized()).finally(() => {
      loadingRef.current = false;
      
      // 计算页面加载时间
      const loadEndTime = performance.now();
      const loadTime = loadEndTime - loadStartTime;
      
      // 上报页面加载时间埋点
      trackingManager.reportPerformance('page_load_time', loadTime);
      logger.log('[App] 页面加载时间:', loadTime, 'ms');
    });
    
    // 加载启动页提示状态
    if (appConfig.startPagePrompt && appConfig.startPagePrompt.enabled) {
      const storageKey = appConfig.startPagePrompt.storageKey || 'hasPromptedStartPage';
      dispatch(fetchStartPagePromptStatus(storageKey));
    }

    // 初始化埋点 - 上报DAU
    const reportDAU = () => {
      trackingManager.reportDAU();
      logger.log('[App] DAU埋点已触发');
    };

    // 延迟上报DAU，避免影响页面加载
    setTimeout(reportDAU, 1000);
  }, [dispatch]);

  // 首次加载时，检查是否需要显示"设置为浏览器首页"的提示
  useEffect(() => {
    // 检查配置是否启用了提示功能
    if (appConfig.startPagePrompt && appConfig.startPagePrompt.enabled) {
      // 判断是否是从扩展程序打开
      const fromExtension = window.location.protocol.includes('-extension:');
      
      // 如果不是从扩展程序打开,才显示提示
      if (!fromExtension) {
        // 使用 storage 替代直接的 localStorage 调用
        const storageKey = appConfig.startPagePrompt.storageKey || 'hasPromptedStartPage';
        const localStorageValue = storage.getItem(storageKey);
        logger.log('[App] 直接从storage读取状态:', localStorageValue);
        
        // 如果用户之前没有被提示过，则在指定时间后显示提示
        if (localStorageValue === null) {
          setTimeout(() => {
            setShowStartPagePrompt(true);
          }, (appConfig.startPagePrompt.delaySeconds || 8) * 1000);
        }
      }
    }
  }, []); // 只在组件挂载时执行一次

  // 获取扩展程序版本号
  useEffect(() => {
    const getExtensionVersion = () => {
      let version = null;
      
      // 方法1: 从window.EXTENSION_VERSION获取（构建时注入）
      if (window.EXTENSION_VERSION) {
        version = window.EXTENSION_VERSION;
        logger.log('[App] 从window.EXTENSION_VERSION获取版本号:', version);
      }
      
      // 方法2: 从meta标签获取
      if (!version) {
        const metaElement = document.querySelector('meta[name="extension-version"]');
        if (metaElement && metaElement.getAttribute('content')) {
          version = metaElement.getAttribute('content');
          logger.log('[App] 从meta标签获取版本号:', version);
        }
      }
      
      // 方法3: 从chrome.runtime.getManifest()获取（仅在扩展环境中可用）
      if (!version && typeof window.chrome !== 'undefined' && window.chrome.runtime && window.chrome.runtime.getManifest) {
        try {
          const manifest = window.chrome.runtime.getManifest();
          if (manifest && manifest.version) {
            version = manifest.version;
            logger.log('[App] 从chrome.runtime.getManifest()获取版本号:', version);
          }
        } catch (error) {
          logger.warn('[App] 无法从chrome.runtime.getManifest()获取版本号:', error);
        }
      }
      
      // 方法4: 从package.json获取（开发环境）
      if (!version) {
        try {
          // 尝试从package.json获取版本号
          const packageJson = require('../package.json');
          if (packageJson && packageJson.version) {
            version = packageJson.version;
            logger.log('[App] 从package.json获取版本号:', version);
          }
        } catch (error) {
          logger.warn('[App] 无法从package.json获取版本号:', error);
        }
      }
      
      // 如果都没有获取到，使用默认版本号
      if (!version) {
        version = '0.1.0';
        logger.warn('[App] 无法获取版本号，使用默认版本号:', version);
      }
      
      // 将版本号存储到全局变量中，供其他组件使用
      window.CURRENT_EXTENSION_VERSION = version;
      logger.log('[App] 最终获取到的扩展程序版本号:', version);
      
      // 上报版本号埋点
      trackingManager.reportEvent('EXTENSION_VERSION_LOADED', {
        version: version,
        source: 'app_initialization'
      });
    };
    
    getExtensionVersion();
  }, []); // 只在组件挂载时执行一次

  // 处理起始页提示关闭
  const handleStartPagePromptClose = (result) => {
    setShowStartPagePrompt(false);
    
    // 无论用户接受还是拒绝，都记录已经提示过
    const storageKey = appConfig.startPagePrompt.storageKey || 'hasPromptedStartPage';
    
    // 更新 Redux 状态
    dispatch(setStartPagePromptStatus({ status: result, storageKey }));
  };

  // 当 currentCategory 变化时，更新Redux中的值
  useEffect(() => {
    if (appConfig.rememberLastSelectedMenu) {
      dispatch(setLastSelectedNav(currentCategory));
    }
  }, [currentCategory, dispatch]);

  const handleCategoryChange = (category) => {
    setCurrentCategory(category);
  };

  const handleSideNavClick = (item) => {
    // 更新当前分类
    setCurrentCategory(item.id);
    
    if (mainContainerRef.current) {
      console.log('jaydenxiaoitem', item);
      
      // 获取item在navItems数组中的实际位置索引
      const actualIndex = navItems.findIndex(navItem => navItem.id === item.id);
      console.log('实际位置索引:', actualIndex, '原item.index:', item.index);
      
      // 使用实际位置索引进行页面切换
      if (actualIndex !== -1) {
        mainContainerRef.current.goToPage(actualIndex);
      }
      
      // 如果是新建的菜单项，使用route进行路由跳转
      if (item.route) {
        mainContainerRef.current.goToCustomPage(item.route, item.text);
      }
    }
  };

  const handleSettingsClick = () => {
    setSettingsVisible(true);
  };

  // 处理Dock中图标点击
  const handleDockIconClick = (app) => {
    // 根据app信息打开对应页面或执行操作
    if (app && app.url) {
      window.open(app.url, '_blank');
    }
  };
  
  // 添加全局拖拽默认处理，防止意外的页面导航
  useEffect(() => {
    // 创建全局样式
    const style = document.createElement('style');
    style.innerHTML = `
      /* 拖拽时禁用用户选择文本 */
      .dragging * {
        user-select: none !important;
      }
      
      /* 拖拽时禁用滚动条 */
      .dragging::-webkit-scrollbar {
        width: 0 !important;
        height: 0 !important;
      }
      
      /* 确保拖拽项保持在前面 */
      [draggable="true"] {
        position: relative;
        z-index: auto !important;
      }
      
      /* 正在拖拽的元素应始终在最前面 */
      body.dragging [draggable="true"]:active {
        z-index: 9999 !important;
      }
      
      /* 当有拖拽时，禁止home-container的滚动条 */
      body.dragging .home-container {
        overflow: hidden !important;
      }
      
      /* 当有拖拽时，禁止apps-wrapper的滚动条 */
      body.dragging .apps-wrapper {
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(style);
    
    const handleDragStart = () => {
      document.body.classList.add('dragging');
      document.body.setAttribute('data-dragging', 'true');
    };
    
    const handleDragEnd = () => {
      document.body.classList.remove('dragging');
      document.body.removeAttribute('data-dragging');
    };
    
    // 添加全局错误处理
    const handleGlobalError = (event) => {
      const error = event.error || event.reason || new Error('Unknown error');
      logger.error('[App] 全局错误:', error);
      
      // 上报错误埋点
      trackingManager.reportError(error, 'global_error');
    };
    
    const handleUnhandledRejection = (event) => {
      const error = event.reason || new Error('Unhandled promise rejection');
      logger.error('[App] 未处理的Promise拒绝:', error);
      
      // 上报错误埋点
      trackingManager.reportError(error, 'unhandled_promise_rejection');
    };
    
    // 添加全局错误监听器
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    // 清理函数
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
    
    const handleDragOver = (e) => {
      e.preventDefault();
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
    };
    
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('dragend', handleDragEnd);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);
    
    return () => {
      document.head.removeChild(style);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('dragend', handleDragEnd);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, []);

  // 创建一个事件监听器，用于Home页面网站项点击
  useEffect(() => {
    const handleWebsiteClick = async (e) => {
      if (e && e.detail) {
        const websiteData = e.detail;
        logger.info('jaydenxiaoclick', "收到点击网站");
        // 如果有URL，在新窗口打开
        if (websiteData.url) {
          window.open(websiteData.url, '_blank');
        }
        
        // 使用全局Dock对象触发添加最近应用
        try {
          if (window.Dock && typeof window.Dock.addRecentApp === 'function') {
            window.Dock.addRecentApp(websiteData);
          }
        } catch (error) {
          logger.error('调用Dock.addRecentApp失败:', error);
        }

        // 上报网站点击埋点
        trackingManager.reportWebsiteClick(websiteData);
        logger.log('[App] 网站点击埋点已触发:', websiteData.name);
      }
    };

    // 监听网站项点击事件
    window.addEventListener('websiteClick', handleWebsiteClick);
    
    // 监听网站删除事件
    const handleWebsiteDelete = (e) => {
      // 可以在这里处理网站删除的全局事件，比如显示通知或刷新特定页面
    };
    
    window.addEventListener('websiteDelete', handleWebsiteDelete);

    return () => {
      window.removeEventListener('websiteClick', handleWebsiteClick);
      window.removeEventListener('websiteDelete', handleWebsiteDelete);
    };
  }, []);

  // 监听登录状态变化
  useEffect(() => {
    logger.log("[App] 登录状态变化:", isLoggedIn);
    
    if (isLoggedIn) {
      logger.log("[App] 用户已登录:", user?.email);
    } else {
      logger.log("[App] 用户已登出");
    }
  }, [isLoggedIn, user]);

  // 监听Auth状态变更事件
  useEffect(() => {
    const handleAuthStateChange = () => {
      logger.log("[App] 收到Auth状态变更事件，强制刷新状态显示");
      // 这里可以添加额外的强制刷新逻辑
    };
    
    // 添加监听authStatusChanged事件
    window.addEventListener('authStatusChanged', handleAuthStateChange);
    
    return () => {
      window.removeEventListener('authStatusChanged', handleAuthStateChange);
    };
  }, []);

  // 监听 force-logout 事件，弹出登录弹窗
  useEffect(() => {
    function onForceLogout() {
      dispatch(logoutUser());
      setShowLoginModal(true);
    }
    window.addEventListener('force-logout', onForceLogout);
    return () => window.removeEventListener('force-logout', onForceLogout);
  }, [dispatch]);

  // 监听 showSettingsDrawer 事件，打开设置面板
  useEffect(() => {
    function onShowSettingsDrawer() {
      setSettingsVisible(true);
    }
    window.addEventListener('showSettingsDrawer', onShowSettingsDrawer);
    return () => window.removeEventListener('showSettingsDrawer', onShowSettingsDrawer);
  }, []);

  return (
    <GlobalMessageApiContext.Provider value={messageApi}>
      {contextHolder}
      <Router>
        <div className="app">
          <TopBar />
          <TopRightActions />
          <SideNav
            activeItem={currentCategory}
            onItemClick={handleSideNavClick}
            onSettingsClick={handleSettingsClick}
          />
          <MainContainer
            ref={mainContainerRef}
            currentCategory={currentCategory}
            onCategoryChange={handleCategoryChange}
          />
          <Dock ref={dockRef} onIconClick={handleDockIconClick} />
          <SettingsDrawer
            visible={settingsVisible}
            onClose={() => setSettingsVisible(false)}
          />
          
          {/* 设置为浏览器首页的提示 */}
          {showStartPagePrompt && (
            <StartPagePrompt 
              onClose={handleStartPagePromptClose}
              delaySeconds={0} // 这里传0是因为我们已经在App组件中处理了延迟
            />
          )}
          {/* 登录弹窗 */}
          <LoginModal
            visible={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onShowRegister={() => {}}
            onShowResetPassword={() => {}}
          />
        </div>
      </Router>
    </GlobalMessageApiContext.Provider>
  );
}

export default App;
